import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { noop } from 'ui-components/default-props/noop';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { ModalPortal } from '../../components/modal-portal';

import { useShipFrom } from '../../hooks/ship-froms/use-ship-from';
import { useBuildInitialShipFrom } from '../hooks/use-build-initial-ship-from';
import { useInventorySources } from '../hooks/use-inventory-sources';

import DefaultLocationCheckbox from '../components/default-location-checkbox';
import Errors from '../components/errors';
import InventorySourceDropdown from '../components/inventory-source-dropdown';
import OriginAddress from '../components/origin-address';
import ReturnAddress from '../components/return-address';
import { ShipFromModalFooter } from '../components/ship-from-modal-footer';
import ShipFromModalHeader from '../components/ship-from-modal-header';
import ShipFromName from '../components/ship-from-name';
import { ShipFromContextProvider } from '../contexts/ship-from-context';

import messages from '../components/messages';
import style from './style.module.less';

/**
 * @param {object} props
 * @param {() => void} [props.onClose]
 * @param {(result: object) => void} [props.onSuccess]
 * @param {(error: object) => void} [props.onError]
 * @param {object | null} [props.prefillAddress]
 * @param {string} [props.shipFromId]
 * @param {string} [props.source]
 * @param {boolean} [props.shouldCreateInventoryWarehouse]
 */
const ShipFromModal = ({
  onClose = noop,
  onSuccess = noop,
  onError = noop,
  prefillAddress = null,
  shipFromId,
  source = '',
  shouldCreateInventoryWarehouse = false,
}) => {
  const headerMessage = shipFromId ? messages.editShipFromModalHeader : messages.addShipFromModalHeader;

  const { fetch, isLoading, sources } = useInventorySources();

  useEffect(() => {
    fetch();
  }, [fetch]);

  const initialShipFrom = useBuildInitialShipFrom(sources, shouldCreateInventoryWarehouse);

  const shipFrom = useShipFrom(shipFromId) ?? initialShipFrom;

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ShipFromModalHeader>
          <FormattedMessage {...headerMessage} />
        </ShipFromModalHeader>
        {isLoading ? (
          <LoadingIndicator className={style['loading-indicator']} />
        ) : (
          <ShipFromContextProvider
            onError={onError}
            onSuccess={onSuccess}
            prefillAddress={prefillAddress}
            shipFrom={shipFrom}
            shouldCreateInventoryWarehouse={shouldCreateInventoryWarehouse}
            source={source}
            sources={sources}
          >
            <ModalBody className={style.body}>
              <Errors />
              <ShipFromName />
              <DefaultLocationCheckbox />
              <OriginAddress />
              <ReturnAddress />
              {!shouldCreateInventoryWarehouse && <InventorySourceDropdown />}
            </ModalBody>
            <ShipFromModalFooter onClose={onClose} />
          </ShipFromContextProvider>
        )}
      </Modal>
    </ModalPortal>
  );
};

export default ShipFromModal;
